import { useState } from 'react';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (userData: { name: string; email: string; phone: string; password: string }) => void;
  onSwitchToEnroll: () => void;
  courseId: string;
}

export default function LoginModal({ isOpen, onClose, onLogin, onSwitchToEnroll, courseId }: LoginModalProps) {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (!isOpen) return null;

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'E-mail é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
    }

    if (!formData.password) {
      newErrors.password = 'Senha é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);

    setTimeout(() => {
      const storedUser = localStorage.getItem('courseUser');

      if (!storedUser) {
        setErrors({ submit: 'Usuário não encontrado. Faça sua inscrição primeiro.' });
        setIsSubmitting(false);
        return;
      }

      const user = JSON.parse(storedUser);

      if (
        user.email.toLowerCase().trim() !== formData.email.toLowerCase().trim() ||
        user.password !== formData.password
      ) {
        setErrors({ submit: 'E-mail ou senha incorretos. Verifique e tente novamente.' });
        setIsSubmitting(false);
        return;
      }

      // Marca como inscrito neste curso
      localStorage.setItem(`enrollment_${courseId}`, 'true');

      setLoginSuccess(true);

      setTimeout(() => {
        onLogin(user);
        onClose();
        setLoginSuccess(false);
        setFormData({ email: '', password: '' });
        setErrors({});
      }, 1400);

      setIsSubmitting(false);
    }, 700);
  };

  const handleClose = () => {
    onClose();
    setFormData({ email: '', password: '' });
    setErrors({});
    setLoginSuccess(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#CC1B1B] to-[#00CCCC] p-6 relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-all cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl">
              <i className="ri-login-circle-line text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Entrar na conta</h2>
              <p className="text-white/90 text-sm mt-1">Acesse seus cursos e progresso</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {loginSuccess ? (
            <div className="py-10 text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                <i className="ri-check-line text-4xl text-green-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Login realizado!</h3>
              <p className="text-gray-500 text-sm">Redirecionando para o seu curso...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  E-mail <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value });
                      setErrors((prev) => ({ ...prev, email: '', submit: '' }));
                    }}
                    placeholder="seu@email.com"
                    className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#CC1B1B]/20 transition-all ${
                      errors.email ? 'border-red-500' : 'border-gray-200 focus:border-[#CC1B1B]'
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Senha */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Senha <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                    <i className="ri-lock-line text-lg"></i>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => {
                      setFormData({ ...formData, password: e.target.value });
                      setErrors((prev) => ({ ...prev, password: '', submit: '' }));
                    }}
                    placeholder="Digite sua senha"
                    className={`w-full pl-10 pr-12 py-3 border-2 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#CC1B1B]/20 transition-all ${
                      errors.password ? 'border-red-500' : 'border-gray-200 focus:border-[#CC1B1B]'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
                  >
                    <i className={showPassword ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'}></i>
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Submit Error */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2 text-red-700 text-sm">
                  <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>
                  <span>{errors.submit}</span>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-[#CC1B1B] to-[#00CCCC] text-white font-bold py-3 rounded-xl hover:shadow-lg transition-all disabled:opacity-60 whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Verificando...
                  </>
                ) : (
                  <>
                    <i className="ri-login-circle-line mr-2"></i>
                    Entrar
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-xs text-gray-400 font-medium">ou</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              {/* Switch to enroll */}
              <button
                type="button"
                onClick={() => {
                  handleClose();
                  onSwitchToEnroll();
                }}
                className="w-full border-2 border-[#CC1B1B] text-[#CC1B1B] font-bold py-3 rounded-xl hover:bg-[#CC1B1B]/5 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-user-add-line mr-2"></i>
                Criar uma conta nova
              </button>
            </form>
          )}
        </div>

        {/* Footer hint */}
        {!loginSuccess && (
          <div className="px-6 pb-6">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center text-amber-600 flex-shrink-0">
                <i className="ri-information-line text-lg"></i>
              </div>
              <p className="text-xs text-amber-800">
                Use o e-mail e senha que você cadastrou ao se inscrever no curso pela primeira vez.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
